// Pure variation filter — search + status + varies + type +
// date-range + include-archived. Mirrors the SOW filter shape so the
// orchestrator can compose both surfaces with the same idioms.
//
// `varies` is a multi-select that mixes the literal `'contract'` token
// with concrete SOW ids. A variation matches when ANY of its targets
// matches a selected token — `'contract'` matches a `kind: 'contract'`
// target, and a SOW id matches a `kind: 'sow'` target with that
// `sowId`. Pre-multi-target variations are read via `readTargets` which
// synthesises a single-element list from the legacy fields.
//
// Date range applies to `effectiveDate` per the brief.

import type { Variation, VariationStatus } from "../../../../types";
import { readTargets } from "./variationTargets";

export type VariationDateRangeFilter =
  | "next-30-days"
  | "past-30-days"
  | "this-quarter"
  | "all-time";

export type VariationTypeFilter = "financial" | "non-financial";

/** A varies-token: either the literal contract reference or a SOW id. */
export type VariesToken = "contract" | string;

export interface VariationFilters {
  status: VariationStatus[];
  varies: VariesToken[];
  type: VariationTypeFilter[];
  dateRange: VariationDateRangeFilter;
  includeArchived: boolean;
}

const DAY_MS = 86_400_000;

export function emptyVariationFilters(): VariationFilters {
  return {
    status: [],
    varies: [],
    type: [],
    dateRange: "all-time",
    includeArchived: false,
  };
}

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string | undefined): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function matchesDateRange(
  variation: Variation,
  range: VariationDateRangeFilter,
  today: Date,
): boolean {
  if (range === "all-time") return true;
  const ms = isoToUTCDay(variation.effectiveDate);
  if (ms === null) return false;
  const todayMs = startOfUTCDay(today);
  if (range === "next-30-days") {
    return ms >= todayMs && ms <= todayMs + 30 * DAY_MS;
  }
  if (range === "past-30-days") {
    return ms < todayMs && ms >= todayMs - 30 * DAY_MS;
  }
  if (range === "this-quarter") {
    const month = today.getUTCMonth();
    const qStartMonth = Math.floor(month / 3) * 3;
    const qStart = Date.UTC(today.getUTCFullYear(), qStartMonth, 1);
    const qEnd = Date.UTC(today.getUTCFullYear(), qStartMonth + 3, 0);
    return ms >= qStart && ms <= qEnd;
  }
  return true;
}

function matchesVaries(
  variation: Variation,
  selected: VariesToken[],
): boolean {
  if (selected.length === 0) return true;
  return readTargets(variation).some((t) => {
    if (t.kind === "contract") return selected.includes("contract");
    return selected.includes(t.sowId);
  });
}

function matchesType(
  variation: Variation,
  selected: VariationTypeFilter[],
): boolean {
  if (selected.length === 0) return true;
  const isFinancial = variation.valueImpact !== null;
  if (isFinancial && selected.includes("financial")) return true;
  if (!isFinancial && selected.includes("non-financial")) return true;
  return false;
}

export function applyVariationFilters(
  variations: readonly Variation[],
  filters: VariationFilters,
  search: string,
  today: Date = new Date(),
): Variation[] {
  const q = search.trim().toLowerCase();
  return variations.filter((v) => {
    if (!filters.includeArchived && v.archivedAt) return false;
    if (filters.status.length > 0 && !filters.status.includes(v.status)) {
      return false;
    }
    if (!matchesVaries(v, filters.varies)) return false;
    if (!matchesType(v, filters.type)) return false;
    if (!matchesDateRange(v, filters.dateRange, today)) return false;
    if (q) {
      const inName = v.name.toLowerCase().includes(q);
      const inNumber = v.number.toLowerCase().includes(q);
      if (!inName && !inNumber) return false;
    }
    return true;
  });
}
